import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { PreviewStatus } from "../../hooks/use-automation-preview"
import type { AutomationPreview } from "../../types/automation.types"

const HEADING_ID = "automation-email-preview-heading"

interface EmailPreviewProps {
  preview: AutomationPreview | null
  status: PreviewStatus
  /** Shown until the server has rendered: the chosen sender and raw subject. */
  fallbackFrom: string
  fallbackSubject: string
  disabled: boolean
  onRetry: () => void
  onOpenFull: () => void
}

function HeaderRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={"flex gap-2"}>
      <dt className={"w-16 shrink-0 text-gray-500"}>{label}</dt>
      <dd className={"m-0 min-w-0 break-words"}>{value}</dd>
    </div>
  )
}

export function EmailPreview({
  preview,
  status,
  fallbackFrom,
  fallbackSubject,
  disabled,
  onRetry,
  onOpenFull
}: EmailPreviewProps) {
  const { t } = useTranslation()
  const rendered = status !== "error" ? preview : null
  return (
    <div
      data-testid={"automation-preview"}
      className={"flex flex-col gap-2 rounded-lg border border-gray-200 p-3"}
    >
      <h4 id={HEADING_ID} className={"m-0 text-start text-sm font-semibold"}>
        {t("settingsAutomation.review.preview.heading")}
      </h4>
      {/* The panel's one live region. Every debounced edit refreshes the
          preview, so only a failure is announced — never routine loading or
          ready, and never the email body. */}
      <p
        data-testid={"automation-preview-status"}
        aria-live={"polite"}
        className={status === "error" ? "m-0 text-sm text-red-600" : "sr-only"}
      >
        {status === "error" ? t("settingsAutomation.review.preview.error") : ""}
      </p>
      {status === "error" && (
        <Button
          size={"small"}
          icon={<ReloadOutlined aria-hidden={"true"} />}
          className={"self-start"}
          disabled={disabled}
          onClick={onRetry}
        >
          {t("settingsAutomation.review.preview.retry")}
        </Button>
      )}
      <dl className={"m-0 flex flex-col gap-1 text-sm"}>
        <HeaderRow
          label={t("settingsAutomation.review.preview.from")}
          value={rendered?.from ?? fallbackFrom}
        />
        <HeaderRow
          label={t("settingsAutomation.review.preview.subject")}
          value={rendered?.subject ?? fallbackSubject}
        />
      </dl>
      {rendered ? (
        <>
          {/* `html` is sanitized by the backend preview renderer before it is
              returned, so it is safe to inject here. */}
          <div
            className={
              "rich-text-content max-h-64 overflow-hidden break-words border-t border-gray-100 pt-2"
            }
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
          <Button
            type={"link"}
            className={"self-start p-0"}
            disabled={disabled}
            onClick={onOpenFull}
          >
            {t("settingsAutomation.review.preview.full")}
          </Button>
        </>
      ) : status === "loading" ? (
        // Not a Spin: that adds a second live region to the panel.
        <LoadingOutlined
          aria-hidden={"true"}
          className={"self-start text-gray-500"}
        />
      ) : (
        status === "idle" && (
          <p className={"m-0 text-sm text-gray-500"}>
            {t("settingsAutomation.review.preview.idle")}
          </p>
        )
      )}
    </div>
  )
}
